
import { Camera, CameraGroup, VideoArchive } from '@/api';
import { ItemsActionNames } from '@/definitions/app/item.actions.name';
import { dataModule } from '@/store/data';
import { NButton } from '@/uikit';
import NIcon from '@/uikit/icons/NIcon.vue';
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { DataSourceLabelsType } from './data.source.labels.type';

type SourceInfo = {
  name: string;
  isExists: boolean;
  key: string;
  groupName: string;  
  icon: string;
  component: string;
  action: (() => void) | undefined;
  id: number;
  groupId: number | null;
};

@Options({
  name: 'DataSourceLabels',
  components: { NIcon, NButton }
})
export default class DataSourceLabels extends Vue {
  @Prop({ type: Array, required: false })
  readonly cameraGroupsIds?: Array<number>;

  @Prop({ type: Array, required: false })
  readonly camerasIds?: Array<number>;

  @Prop({ type: Number, required: false })
  readonly videoArchiveId?: number;

  @Prop({ type: String, default: '/' })
  readonly separator!: string;

  @Prop({ type: Boolean, default: false })
  readonly hasPlay!: boolean;

  @Prop({ type: Boolean, default: true })
  readonly hasIcon!: boolean;


  @Prop({ type: String, default: DataSourceLabelsType.Button })
  readonly type!: DataSourceLabelsType;

  private camerasMap: Record<string, Camera> = {};
  private videosMap: Record<string, VideoArchive> = {};
  private cameraGroupsMap: Record<string, CameraGroup> = {};
  private items: SourceInfo[] = [];

  async mounted(): Promise<void> {
    await this.computeItems();
  }

  @Watch('videoArchiveId')
  @Watch('camerasIds')
  @Watch('cameraGroupsIds')
  async computeItems(): Promise<void> {
    const result: SourceInfo[] = [];

    if (this.camerasIds?.length) {
      this.camerasMap = await dataModule.camerasModule.getMapByIds(this.camerasIds);
      const groupsIds = new Set(Object.values(this.camerasMap).map((camera, i) => (camera?.group)).concat(this.cameraGroupsIds || []));
      this.cameraGroupsMap = await dataModule.cameraGroupsModule.getMapByIds(Array.from(groupsIds));
      result.push(...this.camerasIds.map((id, i) => this.getSomethingInfo(id, 'camera', this.camerasMap[String(id)]?.group ?? (this.cameraGroupsIds || [])[i])));
    } else if (this.videoArchiveId) {
      this.videosMap = await dataModule.videosModule.getMapByIds([this.videoArchiveId]);
      const groupsIds = new Set(Object.values(this.videosMap).map(video => video.camera_group));
      this.cameraGroupsMap = await dataModule.cameraGroupsModule.getMapByIds(Array.from(groupsIds));
      result.push(this.getSomethingInfo(this.videoArchiveId, 'videos', this.videosMap[String(this.videoArchiveId)]?.camera_group ?? (this.cameraGroupsIds || [])[0]));
    } else if (this.cameraGroupsIds?.length) {
      this.cameraGroupsMap = await dataModule.cameraGroupsModule.getMapByIds(this.cameraGroupsIds);
      result.push(...this.cameraGroupsIds.map(id => this.getSomethingInfo(id, 'camera-group', id)));
    }

    this.items = result;
  }

  getSomethingInfo(id: number, type: 'camera' | 'videos' | 'camera-group', groupId: number | null): SourceInfo {
    let name: string;
    let groupName: string;
    let component: string;
    let icon = type;
    let isExists: boolean = false;
    let action: (() => void) | undefined;

    switch (type) {
      case 'camera':
        const camera = this.camerasMap[String(id)];
        isExists = !!camera;
        groupName = this.getCameraGroupName(groupId);
        name = camera?.name ?? this.formattedCameraNotFound;
        component = camera && this.type === DataSourceLabelsType.Button ? 'NButton' : 'div';
        action = camera ? () => this.emitActionShowCamera(id) : undefined;
        break;
      case 'videos':
        const video = this.videosMap[String(id)];
        isExists = !!video;
        groupName = this.getCameraGroupName(groupId);
        name = video?.name ?? this.formattedCameraNotFound;
        component = video && this.type === DataSourceLabelsType.Button ? 'NButton' : 'div';
        action = video && groupName !== this.formattedCameraGroupNotFound ? () => this.emitActionShowVideo(id) : () => {};
        break;
      case 'camera-group':
        icon = 'camera';
        groupName = this.getCameraGroupName(id);
        isExists = false;
        name = this.formattedCameraNotFound;
        component = 'div';
        action = undefined;
        break;
    }

    return {
      name,
      isExists,
      key: `${type}-${id}`,
      groupName,
      icon,
      component,
      action,
      id,
      groupId
    };
  }

  get formattedSeparator() {
    return ` ${this.separator} `;
  }

  get formattedCameraGroupNotFound() {
    return `${this.$t('errors.camera_group.not_found')}`;
  }

  get formattedCameraNotFound() {
    return `${this.$t('errors.camera.not_found')}`;
  }

  getCameraGroupName(id: number | null): string {
    return this.cameraGroupsMap[String(id)]?.name ?? this.formattedCameraGroupNotFound;
  }

  getCameraName(cameraId: number): string {
    return this.camerasMap[String(cameraId)]?.name ?? this.formattedCameraNotFound;
  }

  getVideoArchiveName(id: number): string {
    return this.videosMap[String(id)]?.name ?? this.formattedCameraNotFound;
  }

  emitPlayAction(id: number) {
    this.$emit('action', ItemsActionNames.ShowPlayer, id);
  }

  emitActionShowCamera(id: number) {
    this.$emit('action', ItemsActionNames.ShowItem, { type: 'cameras', rawItem: id });
  }

  emitActionShowVideo(id: number) {
    this.$emit('action', ItemsActionNames.ShowItem, { type: 'videos', rawItem: id });
  }
}
